/**
 * Timestamp diagnostics for CRDT → compile → PDF.
 * Filter DevTools / server logs with: Compile:PDF:TS
 *
 * Backend suspected clock / ordering issues between:
 * - envelope.timestamp (ISO string we send on compile WS)
 * - PDF URL cache-bust ?t= (epoch ms)
 * - CRDT last send/recv (epoch ms)
 * - backend event fields (started_at, etc.)
 */

export const COMPILE_PDF_TS_TAG = "[Compile:PDF:TS]"

export type CompilePdfTsClock = {
  /** Client wall clock as ISO-8601 (same format as WS envelope.timestamp) */
  clientIso: string
  /** Client wall clock as epoch milliseconds (same units as PDF ?t=) */
  clientEpochMs: number
  /** High-res monotonic time when available (ordering within this tab only) */
  monoMs: number
}

export function compilePdfTsClock(): CompilePdfTsClock {
  const clientEpochMs = Date.now()
  return {
    clientIso: new Date(clientEpochMs).toISOString(),
    clientEpochMs,
    monoMs: typeof performance !== "undefined" ? performance.now() : clientEpochMs,
  }
}

export function compilePdfTsLog(
  step: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  const clock = compilePdfTsClock()
  console.log(`${COMPILE_PDF_TS_TAG} ${step} — ${message}`, {
    step,
    ...clock,
    ...data,
  })
}

/** Pull common timestamp-ish fields from backend WS payloads for side-by-side compare. */
export function extractBackendTimeFields(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object") return {}
  const d = data as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of [
    "timestamp",
    "started_at",
    "startedAt",
    "finished_at",
    "finishedAt",
    "completed_at",
    "completedAt",
    "created_at",
    "createdAt",
    "updated_at",
    "updatedAt",
    "pdf_ready_at",
    "pdfReadyAt",
    "compiled_at",
    "compiledAt",
    "at",
  ]) {
    if (d[key] !== undefined) out[key] = d[key]
  }
  return out
}

/** Parse PDF URL cache-bust `t=` query (epoch ms we attach on apply). */
export function parsePdfCacheBust(url: string | null | undefined): number | null {
  if (!url) return null
  try {
    const u = url.startsWith("http")
      ? new URL(url)
      : new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost")
    const t = u.searchParams.get("t")
    if (!t) return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

/** Coerce backend timestamp fields (ISO string, epoch sec/ms, Date-like) to epoch ms. */
export function parseBackendEpochMs(value: unknown): number | null {
  if (value == null) return null
  if (typeof value === "number" && Number.isFinite(value)) {
    // Heuristic: seconds vs milliseconds
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value)
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return parseBackendEpochMs(Number(trimmed))
    }
    const ms = Date.parse(trimmed)
    return Number.isFinite(ms) ? ms : null
  }
  return null
}

/**
 * Pick a shared cache-bust timestamp for PDF URLs so every collaborator fetches
 * the same generation. Prefer backend event times / pdf_url ?t=, else client now.
 */
export function resolvePdfCacheBustMs(params: {
  backendTimes?: Record<string, unknown>
  messageData?: Record<string, unknown> | null
  pdfUrlFromBackend?: string | null
  fallbackEpochMs?: number
}): { cacheBustMs: number; source: string } {
  const { backendTimes = {}, messageData = null, pdfUrlFromBackend, fallbackEpochMs } = params
  const candidates: Array<{ source: string; ms: number }> = []

  const fromUrl = parsePdfCacheBust(pdfUrlFromBackend)
  if (fromUrl != null) candidates.push({ source: "backend_pdf_url_t", ms: fromUrl })

  const bag: Record<string, unknown> = { ...backendTimes, ...(messageData ?? {}) }
  for (const key of [
    "pdf_ready_at",
    "pdfReadyAt",
    "compiled_at",
    "compiledAt",
    "finished_at",
    "finishedAt",
    "completed_at",
    "completedAt",
    "updated_at",
    "updatedAt",
    "started_at",
    "startedAt",
    "timestamp",
    "at",
    "t",
    "recovered_at",
    "refreshed_at",
  ]) {
    const ms = parseBackendEpochMs(bag[key])
    if (ms != null) candidates.push({ source: `backend:${key}`, ms })
  }

  if (candidates.length > 0) {
    // Newest backend signal wins when several fields are present.
    candidates.sort((a, b) => b.ms - a.ms)
    return { cacheBustMs: candidates[0].ms, source: candidates[0].source }
  }

  const fallback = fallbackEpochMs ?? Date.now()
  return { cacheBustMs: fallback, source: "client_now" }
}
